/**
 * The Quotation Commission panel — the four commission percentages, their total, and the markup
 * that sets every line's price.
 *
 * Modelled on the ERP screen Akhil supplied (2026-09-04). All five percentages are REAL fields on
 * the live `Quotation` doctype — `management_commission`, `software_commission`,
 * `port_agent_commission`, `other_charges`, `markup` — of which this app previously read only two.
 *
 * ---- Markup and commission are LINE-level, not a charge on the total (2026-09-04) ----
 *
 * The business rule, from Akhil: "markup and commission are for line items only, not total bill,
 * based on the formula". A line sells at `((cost x markup%) + cost) / ((100 - commission%) / 100)`
 * and the quotation total is the sum of those lines. Nothing is added on top.
 *
 * The SERVER does not implement that. Measured live on QTN-2026-00014 with net 868.66:
 *
 *     markup 30      -> quotation_markup  260.60   (net x 30%)
 *     commission 40  -> total_commission  347.46   (net x 40%)
 *     grand_total                       1,476.72   (net + both)
 *
 * So while `Quotation.markup` and the four commission fields hold a non-zero value, the server
 * bills them a SECOND time on top of line prices that already contain them — AED 800.00 of cost
 * quoted at 1,173.30 rather than 960.00 (the case Akhil reported). Filed as `D27`.
 *
 * Until the server stops doing that, those fields are never written non-zero from this app: the
 * rates drive the line price here and the document keeps them at 0, so `grand_total` equals the
 * line subtotal and the customer is billed exactly what the quotation shows. The cost is that the
 * rates do not persist — they are the same fields that trigger the charge — which is why
 * `applyMarkup` reprices from COST and is therefore idempotent: re-entering 30/40 after a reload
 * produces the same prices rather than compounding them.
 */


import { money } from './money'
import type { QuotationLine } from './types'

export interface CommissionRates {
  management: number
  software: number
  port_agent: number
  other_charges: number
}

const round2 = (n: number): number => Math.round(n * 100) / 100

/** A rate that is absent, NaN or infinite contributes nothing rather than poisoning the sum. */
const rate = (n: unknown): number => (typeof n === 'number' && Number.isFinite(n) ? n : 0)

/**
 * The four commissions, summed — the ERP's own "Total Commission" row (10 + 0 + 0 + 5 = 15).
 *
 * Rounded because three rates of 3.33 otherwise total 9.990000000000002, and a percentage rendered
 * beside real money must not look like a floating-point artefact.
 */
export function totalCommissionPct(rates: CommissionRates): number {
  return round2(rate(rates.management) + rate(rates.software) + rate(rates.port_agent) + rate(rates.other_charges))
}

/**
 * The commission AMOUNT the server will charge — `net × (sum of the four rates) / 100`.
 *
 * Measured live (the numbers are in this module's test): `total_commission` on the doctype is a
 * `Currency` field, not a percentage, and `grand_total = net + quotation_markup + total_commission`.
 * So commission is money the customer pays, and ALL FOUR rates feed it.
 *
 * That last part matters: the client used to pass `management_commission` alone into
 * `previewTotals`, so a quotation carrying software or port-agent commission displayed a total
 * BELOW what the server would actually charge.
 */
export function commissionAmount(netTotal: number, rates: CommissionRates): number {
  const net = typeof netTotal === 'number' && Number.isFinite(netTotal) ? netTotal : 0
  return round2((net * totalCommissionPct(rates)) / 100)
}


/**
 * Reprice every UNLOCKED line at `markupPct` over its own cost, grossed up for commission.
 *
 * `((cost x markup) + cost) / ((100 - commission) / 100)` — Akhil's formula, pinned by its worked
 * example in this module's test. The gross-up is there because commission is paid OUT OF the
 * selling price: a plain markup would leave the business short by the commission's share.
 *
 * ---- Always from COST, never from the current sell price ----
 *
 * That is what makes this idempotent. The rates cannot persist (see the module header), so a
 * purchaser WILL re-enter them after a reload and press Apply again — computing from the current
 * sell price would compound 30% into 69% and there would be no way to tell from the data that it
 * had happened.
 *
 * ---- The lock ----
 *
 * A purchaser sets a price by hand — a negotiated rate, a customer-specific figure — and then
 * changes the markup. Without a lock that price is silently overwritten by an action aimed at
 * every other line. `lockedLineIds` is what the line-level lock and the lock-all control fill.
 * It has NO field on `Quotation Line` (27 fields, checked live), so it is held for the session
 * only and does not survive a reload — asked for as `D24`.
 *
 * Two lines are skipped besides the locked ones:
 *
 *   - a cost of 0, meaning "we were never told what it cost" (the reading `marginGate.ts` and
 *     `marginFloor.ts` both use). A markup over nothing is nothing, and would zero a line that may
 *     carry a hand-set price.
 *   - a non-numeric markup, which is a half-typed input, not an instruction.
 *
 * A markup of ZERO is not skipped: "sell at cost" is a real instruction, unlike a floor of zero
 * which means "no floor".
 */
export function applyMarkup(
  lines: QuotationLine[],
  markupPct: number,
  lockedLineIds: ReadonlySet<string>,
  commissionPct = 0,
): QuotationLine[] {
  if (!Number.isFinite(markupPct)) return lines

  const retained = (100 - commissionPct) / 100
  // A commission of 100% (or more) would divide by zero or flip the sign. Left ungrossed rather
  // than producing an infinite price.
  const grossUp = retained > 0 ? retained : 1

  return lines.map((line) => {
    if (lockedLineIds.has(line.line_id)) return line
    const cost = line.cost_unit.amount
    if (!Number.isFinite(cost) || cost <= 0) return line
    return {
      ...line,
      sell_unit: money(round2((cost * (1 + markupPct / 100)) / grossUp), line.cost_unit.currency),
    }
  })
}

/**
 * The cost→sell ratio this quotation is actually running at, or `null` if nothing is priced yet.
 *
 * A line added mid-revision is created at cost 0.00 and, once its recorded supplier price is
 * written, sits at cost with no margin — the server derives `sell_unit = cost_unit` when no margin
 * is sent. Quoting one line at cost beside twelve at 64% over it is not a price anybody chose.
 *
 * Read off the LINES rather than from a markup rate, because the rate no longer persists:
 * `Quotation.markup` and the commission fields are the same fields that trigger the server's
 * document-level charge (`D27`), so `CommissionPanel` holds them in session state and the document
 * keeps them at 0. The prices themselves are the only durable record of what was applied — which
 * is what "the current markup running" means.
 *
 * The MEDIAN, not the mean: a hand-set or negotiated price is exactly the kind of outlier that
 * should not drag every subsequent line with it.
 */
export function prevailingSellRatio(
  lines: readonly { cost_unit: { amount: number }; sell_unit: { amount: number } }[],
): number | null {
  const ratios = lines
    .filter((l) => l.cost_unit.amount > 0 && l.sell_unit.amount > 0)
    .map((l) => l.sell_unit.amount / l.cost_unit.amount)
    .sort((a, b) => a - b)

  if (ratios.length === 0) return null
  const mid = Math.floor(ratios.length / 2)
  return ratios.length % 2 === 1 ? ratios[mid] : (ratios[mid - 1] + ratios[mid]) / 2
}

/**
 * The markup the quotation is ACTUALLY running at, read back off its own prices.
 *
 * The rate itself cannot be stored. `Quotation.markup` and the four commission fields are the same
 * fields that trigger the server's document-level charge (`D27`), so this app pins them at 0 and
 * holds the purchaser's rates in session state — which means they are gone after a reload, and the
 * panel would otherwise read 0% over a quotation whose every line carries 64%.
 *
 * Measured: there is nowhere else to put them. Of the `Quotation` doctype's 53 scalar fields the
 * only free text ones are `customer_reference_no` (the CUSTOMER's own reference) and `terms` (the
 * T&C the customer reads); writing pricing state into either would corrupt a customer-facing
 * field.
 *
 * So the prices are the record. This reports the combined uplift — markup and the commission
 * gross-up together — because that is what a sell price contains; the two cannot be separated back
 * out of one number, and pretending otherwise would put a made-up split on screen.
 */
export function effectiveMarkupPct(
  lines: readonly { cost_unit: { amount: number }; sell_unit: { amount: number } }[],
): number | null {
  const ratio = prevailingSellRatio(lines)
  return ratio === null ? null : round2((ratio - 1) * 100)
}

/**
 * What a commission-rate edit writes (Akhil, 2026-09-18: "not saving in backend —
 * management_commission, software_commission, port_agent_commission, other_charges").
 *
 * The four were session-only while the server BILLED from them — `D27`: storing 30 there
 * charged the customer 30 on top of line prices that already contained it. Measured
 * 2026-09-18 on QTN-2026-00063: writing 5 / 2 / 4 / 1.5 left `total`, `net_total`,
 * `grand_total` and `total_commission` exactly as they were, so the four are inert storage now
 * and can carry the purchaser's split. `line_commission`, their total, stays — it is what the
 * line prices are grossed up for and what the older documents already hold.
 */
export function commissionRatesPatch(rates: CommissionRates): {
  management_commission: number
  software_commission: number
  port_agent_commission: number
  other_charges: number
  line_commission: number
} {
  return {
    management_commission: rates.management,
    software_commission: rates.software,
    port_agent_commission: rates.port_agent,
    other_charges: rates.other_charges,
    line_commission: totalCommissionPct(rates),
  }
}

/**
 * What the panel opens on: the four stored rates when any is set; for a document from before
 * they were stored (only `line_commission` on it), that total under Management — the one honest
 * place for a figure whose split was never recorded.
 */
export function seedCommissionRates(stored: CommissionRates & { line_commission?: number }): CommissionRates {
  const four = { management: stored.management, software: stored.software, port_agent: stored.port_agent, other_charges: stored.other_charges }
  if (totalCommissionPct(four) > 0) return four
  return { management: stored.line_commission ?? 0, software: 0, port_agent: 0, other_charges: 0 }
}

/**
 * A document priced before the four rates were stored (2026-09-18): a total on
 * `line_commission`, nothing in the four. The panel opens on that total under Management so
 * "Apply to every line" keeps grossing up at the right rate — and SAYS it is the total, not a
 * Management rate (Akhil, 2026-09-18: "total saved to management commission").
 */
export function commissionSplitUnrecorded(stored: CommissionRates & { line_commission?: number }): boolean {
  return totalCommissionPct(stored) === 0 && (stored.line_commission ?? 0) > 0
}
